<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import TextForm from './components/TextForm.vue';
import { useLoading } from '@/utils/useLoading';
import { onMounted } from 'vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import { type TextDto, TextDtoTypeEnum } from '@/api/generator';
import { useToken } from '@/utils/tokens/tokens';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';
import { useRouter } from 'vue-router';
import { toCreateUpdateTextDto } from './mappings';
import { translateEntityProperty } from '@/utils/helper.ts';
import { backendValidator } from '@/validation/validators/backendValidator';

export interface TextEditViewProps {
  id: string;
}

const props = defineProps<TextEditViewProps>();
const router = useRouter();

const { form } = useVeeForm<TextDto>({
  schemaName: 'TextDto',
  mode: 'edit',
  customValidateFunctions: [
    backendValidator((textDto, errorHandlers) =>
      api.textsApi.validateText(
        { createUpdateTextDto: toCreateUpdateTextDto(textDto) },
        errorHandlers,
      ),
    ),
  ],
});

const { action: loadText, loading } = useLoading(async () => {
  const { result } = await api.textsApi.getText({ id: props.id }, [
    entityNotFoundErrorHandler(router),
  ]);

  if (result) {
    form.resetForm({ values: result });
  }
}, true);

const { values, handleSubmit } = form;

const token = useToken({
  id: props.id,
  $type:
    TextDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoTextDtoPtwPtw100AppZeroApplicationShared,
});

onMounted(async () => token.request());

token.on.initialized(async () => loadText());

token.on.inUse(() => {
  router.push({
    name: routenames.texts.overview,
  });
});

const submit = handleSubmit(
  async (text) => {
    const { success } = await api.textsApi.updateText(
      {
        id: props.id,
        createUpdateTextDto: toCreateUpdateTextDto(text),
      },
      [formBackendErrorHandler(form)],
    );

    if (success) {
      await router.push({
        name: routenames.texts.overview,
      });
    }
  },
  {
    withReferenceCheck: true,
  },
);
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle
          :subtitle="$t('Text_one')"
          :title="translateEntityProperty('name', values)"
          :titleId="values.iuid"
        />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.texts.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <TextForm />
    </BasePage>
  </form>
</template>
